// =============================================================================
// AI ASSISTANT STORE (ZUSTAND)
// =============================================================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Message, Conversation, AgentType } from './types';

interface AIAssistantState {
  // State
  isOpen: boolean;
  isLoading: boolean;
  activeAgentType: AgentType;
  conversations: Conversation[];
  currentConversationId: string | null;
  inputValue: string;

  // Computed
  currentConversation: () => Conversation | null;
  currentMessages: () => Message[];

  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  setActiveAgent: (agentType: AgentType) => void;
  setInputValue: (value: string) => void;

  // Conversation actions
  createConversation: (agentType: AgentType) => Conversation;
  selectConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  clearConversations: () => void;

  // Message actions
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;

  // Reset
  reset: () => void;
}

const generateId = () => crypto.randomUUID();

export const useAIAssistantStore = create<AIAssistantState>()(
  immer((set, get) => ({
    // Initial state
    isOpen: false,
    isLoading: false,
    activeAgentType: 'general',
    conversations: [],
    currentConversationId: null,
    inputValue: '',

    // Computed
    currentConversation: () => {
      const state = get();
      return state.conversations.find((c) => c.id === state.currentConversationId) || null;
    },

    currentMessages: () => {
      const conversation = get().currentConversation();
      return conversation?.messages || [];
    },

    // Panel actions
    toggle: () =>
      set((state) => {
        state.isOpen = !state.isOpen;
      }),

    open: () =>
      set((state) => {
        state.isOpen = true;
      }),

    close: () =>
      set((state) => {
        state.isOpen = false;
      }),

    setActiveAgent: (agentType) =>
      set((state) => {
        state.activeAgentType = agentType;
      }),

    setInputValue: (value) =>
      set((state) => {
        state.inputValue = value;
      }),

    // Conversation actions
    createConversation: (agentType) => {
      const newConversation: Conversation = {
        id: generateId(),
        title: 'New Conversation',
        agentType,
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      set((state) => {
        state.conversations.unshift(newConversation);
        state.currentConversationId = newConversation.id;
        state.activeAgentType = agentType;
      });

      return newConversation;
    },

    selectConversation: (id) =>
      set((state) => {
        const conversation = state.conversations.find((c) => c.id === id);
        if (conversation) {
          state.currentConversationId = id;
          state.activeAgentType = conversation.agentType;
        }
      }),

    deleteConversation: (id) =>
      set((state) => {
        state.conversations = state.conversations.filter((c) => c.id !== id);
        if (state.currentConversationId === id) {
          state.currentConversationId = state.conversations[0]?.id || null;
        }
      }),

    clearConversations: () =>
      set((state) => {
        state.conversations = [];
        state.currentConversationId = null;
      }),

    // Message actions
    sendMessage: async (content) => {
      const state = get();
      let conversationId = state.currentConversationId;

      // Create conversation if none exists
      if (!conversationId) {
        const newConversation = get().createConversation(state.activeAgentType);
        conversationId = newConversation.id;
      }

      // Add user message
      const userMessage: Message = {
        id: generateId(),
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
        status: 'sent' as const,
      };

      set((state) => {
        const conversation = state.conversations.find((c) => c.id === conversationId);
        if (conversation) {
          conversation.messages.push(userMessage);
          conversation.updatedAt = new Date().toISOString();

          // Update title from first message
          if (conversation.messages.length === 1) {
            conversation.title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
          }
        }
        state.isLoading = true;
        state.inputValue = '';
      });

      // Simulate AI response
      await simulateAIResponse(get, set, conversationId!);
    },

    addMessage: (message) =>
      set((state) => {
        const conversation = state.conversations.find(
          (c) => c.id === state.currentConversationId
        );
        if (conversation) {
          conversation.messages.push({
            ...message,
            id: generateId(),
            timestamp: new Date().toISOString(),
          });
          conversation.updatedAt = new Date().toISOString();
        }
      }),

    updateMessage: (messageId, updates) =>
      set((state) => {
        const conversation = state.conversations.find(
          (c) => c.id === state.currentConversationId
        );
        if (conversation) {
          const message = conversation.messages.find((m) => m.id === messageId);
          if (message) {
            Object.assign(message, updates);
          }
        }
      }),

    // Reset
    reset: () =>
      set((state) => {
        state.isOpen = false;
        state.isLoading = false;
        state.activeAgentType = 'general';
        state.conversations = [];
        state.currentConversationId = null;
        state.inputValue = '';
      }),
  }))
);

// Simulate AI response (in real app, this would call the AI gateway)
async function simulateAIResponse(
  get: () => AIAssistantState,
  set: (fn: (state: AIAssistantState) => void) => void,
  conversationId: string
) {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 1000));

  const agentType = get().activeAgentType;
  const responses: Record<AgentType, string[]> = {
    schema: [
      "I'll help you design that data model. Based on your requirements, I suggest creating the following entities:\n\n1. **LeaveRequest** - Main entity for leave requests\n2. **Employee** - Person requesting leave\n3. **Approver** - Manager who approves\n\nWould you like me to generate the full schema with fields and relations?",
      "Here's a suggested schema for your customer entity:\n\n```\nCustomer {\n  id: UUID\n  name: string\n  email: string (unique)\n  phone: string?\n  address: Address (relation)\n  contacts: Contact[] (one-to-many)\n  createdAt: datetime\n}\n```\n\nShall I add more fields or create the relations?",
    ],
    workflow: [
      "I'll create an employee onboarding workflow for you. Here's the suggested structure:\n\n1. **Trigger**: New employee record created\n2. **Send welcome email**\n3. **Create accounts** (IT systems)\n4. **Assign mentor**\n5. **Schedule orientation**\n6. **Wait 7 days**\n7. **Send feedback survey**\n\nWant me to add this to your workflow canvas?",
      "For the approval workflow with notifications, I recommend:\n\n1. **Condition**: Check request amount\n2. **Branch**: Single vs multi-level approval\n3. **Email node**: Notify approver\n4. **Approval node**: Wait for decision\n5. **Email node**: Notify requester of result\n\nShould I generate this workflow?",
    ],
    document: [
      "I'll generate a contact form template for you. It will include:\n\n- Name (required)\n- Email (required, validated)\n- Phone (optional)\n- Subject (dropdown)\n- Message (text area, required)\n- Consent checkbox\n\nThe form will have proper validation and responsive layout. Should I create it?",
      "I can help you create documentation. What type would you like?\n\n1. **API Documentation** - Endpoint specs\n2. **User Guide** - How-to instructions\n3. **Technical Spec** - Architecture details\n4. **Release Notes** - Change log\n\nLet me know which one you need.",
    ],
    general: [
      "To add a relation between two entities:\n\n1. Select the source entity in Data Modeler\n2. Click 'Add Field'\n3. Choose 'Relation' as the field type\n4. Select the target entity\n5. Choose the relation type (one-to-one, one-to-many, many-to-many)\n\nNeed help with anything else?",
      "Welcome to DevFactory Studio! Here's what you can do:\n\n- **Data Modeler**: Design your data schemas with drag & drop\n- **Workflow Designer**: Create automated workflows visually\n- **Form Builder**: Build forms without coding\n\nHow can I help you get started?",
    ],
  };

  const agentResponses = responses[agentType];
  const responseContent = agentResponses[Math.floor(Math.random() * agentResponses.length)] || 'I can help you with that.';

  const assistantMessage: Message = {
    id: generateId(),
    role: 'assistant',
    content: responseContent,
    agentType,
    timestamp: new Date().toISOString(),
    status: 'sent' as const,
  };

  set((state) => {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (conversation) {
      conversation.messages.push(assistantMessage);
      conversation.updatedAt = new Date().toISOString();
    }
    state.isLoading = false;
  });
}
